// callSessionStore.js — the SINGLE in-memory source of truth for the "during a call" panel (PP-0/PP-2/PP-3).
//
// Joey (Nov 3 2026): during a call the blue side menu tucks away and the phone panel takes its place on
// the left; a "Menu" button brings the side menu back WITHOUT ending the call; the account page stays
// fully usable. On Call → status "On a Call"; End Call → "After-Call Work" + the disposition; the rep
// clicks "I'm Ready for the next call" to go Ready. This store holds the one active-call session so the
// shell (Dashboard) and the panel — rendered in different parts of the tree — always agree. React's
// useSyncExternalStore keeps every subscriber in sync (same pattern as presenceStore.js).
//
// Ephemeral + in-tab: a call session is NOT server-persisted here. The call OUTCOME is still logged
// exactly once through the existing disposition path (PhoneProvider.openManualLog → CallOutcomeModal) —
// this store never counts a call and never writes a disposition to the backend.
//
// PP-3 lifecycle this store OWNS (so it survives the panel un/re-mounting when the Menu is toggled):
//   • the top-bar system-presence overlay ('on_call' while talking, 'acw' while wrapping up),
//   • the call-active idle suppression epoch (armed while On a Call + the first N minutes of ACW),
//   • the After-Call-Work overage handoff — at N minutes it stops suppressing and hands ONE idle period
//     to the EXISTING idle writer (ActivityTrackerContext reads acwOverageSince()).
import { useSyncExternalStore } from 'react';
import { setSystemPresence } from '../presenceStore';
import {
  armCallSuppression, clearCallSuppression, setAcwOverageSince, setAcwStartedSince, clearAcwOverage,
} from '../../constants/presence';

const EMPTY = {
  active: false,
  phase: '',        // '' | 'calling' (PP-5 Zoom: dialing, not yet connected) | 'active' (on a call) | 'acw'
  entityType: '',   // e.g. 'collections_account'
  entityId: '',
  name: '',         // display name for non-collections records (the panel only fetches collections accounts)
  number: '',
  startedAt: 0,     // epoch ms when the call CONNECTED (panel renders elapsed from this). 0 while 'calling'.
  acwStartedAt: 0,  // epoch ms when After-Call Work began
  acwLimitMs: 0,    // owner "after-call work limit" in ms (default 3 min), captured at End Call
  onHold: false,
  menuOpen: false,  // Menu button brings the side menu back WITHOUT ending the call
  onLogged: null,   // optional account-page refresh callback, run after the outcome is logged
  // ── PP-5 (Zoom Smart Embed) — lifted into the store so the always-mounted ZoomEmbed AND the panel
  //    (which unmounts when Menu is open) both read the same truth:
  guideOutcome: '',   // the Call Guide's picked outcome → pre-fills the "Log this call" disposition
  outcomeLogged: false, // flips true once the one-count disposition is saved (hides "Log call outcome")
  zoomCallId: '',     // the Zoom callId captured from zp-call-* events — the ledger provider_call_id
  dialRequest: null,  // {number, nonce} when a Zoom dial is pending; consumed by ZoomEmbed. NOT persisted.
  reconnecting: false, // true only for a 'calling' call restored after a page refresh (state unconfirmed)
  callKey: 0,         // stable per-call identity for the embedded Call Guide (does NOT change at connect)
  // ── PP-5 Release 2 (inbound screen-pop) ─────────────────────────────────────────
  direction: 'outbound', // 'outbound' (we dialed) | 'inbound' (incoming screen-pop)
  matchStatus: '',    // ledger match_status: dialed_from_record | auto_single | rep_picked | unmatched
  locked: false,      // inbound match is a LOCKED collections account → show only "Locked account"
  outsourcingBlocked: false, // inbound caller is an outsourcing consumer → the BRAIN-CALL notice, no log vs consumer
  partnerCompany: '', // the outsourcing partner company name for that notice
  department: '',     // display chip for a matched inbound record
};

// Module flag mirrored from phone settings (set by PhoneProvider via setZoomConnected). When true,
// startCall enters the 'calling' phase and requests a Zoom dial instead of the manual 'active' flow.
// Default false => every call site keeps today's manual behavior byte-for-byte.
let zoomConnected = false;
export function setZoomConnected(on) { zoomConnected = !!on; }

let _dialNonce = 0;

let snapshot = EMPTY;
const listeners = new Set();

// ── Refresh persistence (R2 / Zoom-readiness) ────────────────────────────────────
// A live call must survive a page refresh (required before Zoom Smart Embed — a reload must never
// lose a call in progress). We persist the session to sessionStorage (PER-TAB) and re-hydrate on
// module load. startedAt / acwStartedAt are epoch ms, so the timer and ACW countdown continue
// exactly across the reload. The blob is stamped with the current user id; on hydrate we restore ONLY
// if it still matches the logged-in user, so a different user on the same tab never inherits a call.
// (resetCallSession on login/logout already clears it; the id stamp is defense-in-depth.) onLogged is
// a function (an account-page refresh callback) — it is NOT serialized and comes back null after a
// reload, which is correct: the page reloaded anyway. Cleared whenever the session goes inactive.
const SESSION_KEY = 'tc_call_session';

function currentUserId() {
  try { return (JSON.parse(localStorage.getItem('user') || '{}').id) || ''; } catch { return ''; }
}

function persistSession() {
  try {
    if (snapshot.active) {
      // onLogged is a fn (not serializable); dialRequest is a one-shot that must NOT re-fire a dial
      // after a refresh — drop both from the persisted blob.
      const { onLogged, dialRequest, ...rest } = snapshot;
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({ ...rest, _uid: currentUserId() }));
    } else {
      sessionStorage.removeItem(SESSION_KEY);
    }
  } catch { /* sessionStorage unavailable (private mode) — a refresh just loses the panel, no crash */ }
}

function hydrateSession() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return;
    const saved = JSON.parse(raw);
    if (!saved || !saved.active) { sessionStorage.removeItem(SESSION_KEY); return; }
    // A different user on the same tab must NEVER inherit a restored call.
    if ((saved._uid || '') !== currentUserId()) { sessionStorage.removeItem(SESSION_KEY); return; }
    const { _uid, onLogged, dialRequest, ...rest } = saved;  // drop the stamp + any stale fn / one-shot dial
    snapshot = { ...EMPTY, ...rest, onLogged: null, dialRequest: null,
                 reconnecting: rest.phase === 'calling' };  // a restored dialing call is unconfirmed
    // Re-assert the top-bar overlay. A 'calling' call restored after a reload is UNCONFIRMED — the Zoom
    // iframe reloaded and its real call state is unknown — so do NOT re-arm idle suppression for it
    // (ZoomEmbed shows "Reconnecting…" and a short timeout resolves it). Only a connected call ('active')
    // or wrap-up ('acw') re-arms the suppression/overage loop so working time + the ACW clock continue.
    setSystemPresence(snapshot.phase === 'acw' ? 'acw' : 'on_call');
    if (snapshot.phase === 'active' || snapshot.phase === 'acw') startLoop();
  } catch { try { sessionStorage.removeItem(SESSION_KEY); } catch { /* ignore */ } }
}

// ── Call-active suppression + ACW-overage loop ───────────────────────────────────
// A short tick arms the suppression epoch (so On a Call + the first N min of ACW never open an idle
// period) and, once ACW passes the limit, hands ONE overage idle period to the existing writer.
const CALL_TICK_MS = 5000;
let loopId = null;

function loopTick() {
  if (!snapshot.active) { stopLoop(); return; }
  if (snapshot.phase === 'acw' && snapshot.acwStartedAt && snapshot.acwLimitMs) {
    if (Date.now() - snapshot.acwStartedAt >= snapshot.acwLimitMs) {
      // After-Call Work exceeded the owner limit → stop suppressing (the overage is now idle). The
      // overage DEADLINE was persisted up front by beginAcw (setAcwOverageSince), so ActivityTracker-
      // Context opens the one idle period / shows the reason-required popup regardless of this in-tab
      // loop — meaning it still fires after a page refresh. Never a second writer.
      clearCallSuppression();
      stopLoop();
      return;
    }
  }
  armCallSuppression();
}

function startLoop() {
  armCallSuppression();
  if (loopId) clearInterval(loopId);
  loopId = setInterval(loopTick, CALL_TICK_MS);
}

function stopLoop() {
  if (loopId) { clearInterval(loopId); loopId = null; }
}

function emit() {
  persistSession();   // keep the per-tab snapshot in sessionStorage so a refresh restores the panel
  listeners.forEach((l) => { try { l(); } catch { /* ignore */ } });
}

// Begin a call session (opens the panel). Replaces any prior session — one active call per tab.
// OFF (zoomConnected=false): identical to today — phase 'active', timer starts immediately.
// ON  (zoomConnected=true):  phase 'calling' ("Calling…"), timer deferred until zp-call-connected
//   (markConnected), and a one-shot dialRequest is queued for ZoomEmbed to post zp-make-call.
export function startCall({ entityType, entityId, number, name, onLogged } = {}) {
  const base = {
    ...EMPTY,
    active: true,
    entityType: entityType || '',
    entityId: entityId || '',
    name: name || '',
    number: number || '',
    callKey: Date.now(),   // stable Call-Guide key for the whole call (survives the connect transition)
    onLogged: typeof onLogged === 'function' ? onLogged : null,
  };
  if (zoomConnected) {
    snapshot = {
      ...base,
      phase: 'calling',
      direction: 'outbound',
      matchStatus: 'dialed_from_record',
      startedAt: 0,                                   // timer starts at connect, not at dial
      dialRequest: { number: number || '', nonce: ++_dialNonce },
    };
  } else {
    snapshot = { ...base, phase: 'active', startedAt: Date.now(), direction: 'outbound' };
  }
  setSystemPresence('on_call');   // top bar shows "On a Call" (dialing counts as on a call)
  clearAcwOverage();
  startLoop();                     // placing/holding a call is working time — never idle
  emit();
}

// ── PP-5 Release 2: inbound screen-pop ───────────────────────────────────────────
// Open the panel for an INCOMING call. phase 'calling' + direction 'inbound' renders "Incoming call";
// the rep answers inside the Zoom embed, which fires zp-call-connected → markConnected. A ring that
// ends without a connect is a MISSED inbound (ZoomEmbed logs it once; see handleEnded there).
export function startInboundCall({
  entityType, entityId, number, name, matchStatus, locked, outsourcingBlocked, partnerCompany, department,
} = {}) {
  snapshot = {
    ...EMPTY,
    active: true,
    phase: 'calling',
    direction: 'inbound',
    entityType: entityType || '',
    entityId: entityId || '',
    name: name || '',
    number: number || '',
    callKey: Date.now(),
    matchStatus: matchStatus || 'unmatched',
    locked: !!locked,
    outsourcingBlocked: !!outsourcingBlocked,
    partnerCompany: partnerCompany || '',
    department: department || '',
  };
  setSystemPresence('on_call');   // handling an incoming call is working time
  clearAcwOverage();
  startLoop();
  emit();
}

// Map ONE /api/phone/lookup match to an inbound screen-pop. Outsourcing consumers get the BRAIN-CALL
// notice with NO entity (never logged against the consumer); a locked collections account shows only
// "Locked account"; everything else opens on the matched record.
export function startInboundFromMatch(match, number, matchStatus) {
  if (!match) { startInboundUnknown(number); return; }
  if (match.entity_type === 'outsourcing_consumer') {
    startInboundCall({
      number, matchStatus: 'unmatched', outsourcingBlocked: true,
      partnerCompany: match.partner_company || match.partner_name || '',
      name: match.display_name || '',
    });
    return;
  }
  if (match.locked) {
    startInboundCall({
      entityType: 'collections_account', entityId: match.entity_id, number,
      name: 'Locked account', locked: true, matchStatus: matchStatus || 'auto_single',
    });
    return;
  }
  startInboundCall({
    entityType: match.entity_type, entityId: match.entity_id, number,
    name: match.display_name || '', department: match.department || '',
    matchStatus: matchStatus || 'auto_single',
  });
}

export function startInboundUnknown(number) {
  startInboundCall({ number, name: 'Unknown caller', matchStatus: 'unmatched' });
}

// Zoom: the call connected (zp-call-connected-event). Promote 'calling' → 'active' and start the timer
// from the connect moment. No-op unless we are mid-call-attempt (idempotent on repeat events).
export function markConnected(connectedAtMs) {
  if (!snapshot.active) return;
  if (snapshot.phase !== 'calling' && snapshot.phase !== 'active') return;
  if (snapshot.phase === 'active' && snapshot.startedAt) return;  // already connected
  snapshot = {
    ...snapshot, phase: 'active', reconnecting: false,
    startedAt: Number(connectedAtMs) > 0 ? Number(connectedAtMs) : Date.now(),
  };
  setSystemPresence('on_call');
  startLoop();
  emit();
}

// Zoom: capture the provider callId (the phone_calls ledger key) from a zp-call-* event.
export function attachZoomCallId(id) {
  if (!snapshot.active || !id || snapshot.zoomCallId === id) return;
  snapshot = { ...snapshot, zoomCallId: String(id) };
  emit();
}

// The Call Guide's picked outcome (lifted here so ZoomEmbed can pre-fill the disposition on zp-call-ended
// even when the panel is unmounted behind the Menu).
export function setGuideOutcome(v) {
  if (!snapshot.active || snapshot.guideOutcome === (v || '')) return;
  snapshot = { ...snapshot, guideOutcome: v || '' };
  emit();
}

// Flip once the one-count disposition has been saved (hides the re-log button; never a 2nd count).
export function setOutcomeLogged(b) {
  if (!snapshot.active || snapshot.outcomeLogged === !!b) return;
  snapshot = { ...snapshot, outcomeLogged: !!b };
  emit();
}

// ZoomEmbed consumes the pending dial exactly once (returns it and clears it). Returns null if none.
export function consumeDialRequest() {
  const dr = snapshot.dialRequest;
  if (!dr) return null;
  snapshot = { ...snapshot, dialRequest: null };
  emit();
  return dr;
}

// End Call → enter After-Call Work. The call is over but the panel stays open for the disposition and
// the "I'm Ready for the next call" step. `limitMinutes` is the owner After-call-work limit (default 3).
export function beginAcw(limitMinutes) {
  if (!snapshot.active) return;
  if (snapshot.phase === 'acw') return;  // idempotent — a double End Call must not reset the ACW clock
  const mins = Number(limitMinutes) > 0 ? Number(limitMinutes) : 3;
  const acwStartedAt = Date.now();
  const acwLimitMs = mins * 60 * 1000;
  snapshot = {
    ...snapshot,
    phase: 'acw',
    onHold: false,
    acwStartedAt,
    acwLimitMs,
  };
  setSystemPresence('acw');       // top bar shows "After-Call Work"
  // Persist the overage DEADLINE (the moment overage begins) UP FRONT. The idle detector
  // (ActivityTrackerContext) reads this from localStorage and fires the reason-required popup at the
  // deadline REGARDLESS of mouse/keyboard activity — and still fires if the rep refreshed the page
  // (the in-tab suppression loop would be gone, but the deadline survives in localStorage). Cleared on
  // Ready/finish/new-call so it never fires if the rep clicks Ready before the limit.
  setAcwOverageSince(acwStartedAt + acwLimitMs);
  // Persist the ACW START too, so the idle /start call can send acw_started_at and the server clamps
  // the overage to the OWNER's limit mark (never the client's claimed deadline). Cleared on finish/Ready.
  setAcwStartedSince(acwStartedAt);
  // loop keeps arming suppression until the deadline, then stops (the overage becomes idle).
  emit();
}

// Finish the call entirely (the rep clicked "I'm Ready for the next call"). Clears the session and all
// lifecycle side-effects. The caller is responsible for setting the real presence back to Ready.
export function finishCall() {
  if (!snapshot.active) return;
  stopLoop();
  clearCallSuppression();
  clearAcwOverage();
  setSystemPresence('');          // drop the overlay → the real status (Ready) shows through
  snapshot = EMPTY;
  emit();
}

// Legacy alias — a hard end that clears everything (kept for callers that just want the panel gone).
export function endCall() {
  finishCall();
}

export function toggleMenu() {
  if (!snapshot.active) return;
  snapshot = { ...snapshot, menuOpen: !snapshot.menuOpen };
  emit();
}

export function setMenuOpen(open) {
  if (!snapshot.active || snapshot.menuOpen === !!open) return;
  snapshot = { ...snapshot, menuOpen: !!open };
  emit();
}

export function setHold(onHold) {
  if (!snapshot.active || snapshot.onHold === !!onHold) return;
  snapshot = { ...snapshot, onHold: !!onHold };
  emit();
}

// Clear on user switch (logout/login) — the store is a module-level singleton and this app's
// logout/login are SPA-only (no reload), so without this a second employee could inherit a stale panel.
export function resetCallSession() {
  stopLoop();
  clearCallSuppression();
  clearAcwOverage();
  setSystemPresence('');
  snapshot = EMPTY;
  emit();
}

function subscribe(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function getSnapshot() {
  return snapshot;
}

export function useCallSession() {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

// Restore a live call after a page refresh (per-tab, same-user only). Runs once at module load.
hydrateSession();
